import { ApiError, api } from "./api";
import type { Profile } from "./types";

export type OnboardingStep = "profile" | "preferences" | "complete";

export function onboardingPath(step: Exclude<OnboardingStep, "complete">) {
  return step === "profile" ? "/onboarding/profile" : "/onboarding/preferences";
}

function hasText(value: unknown, minLength = 1) {
  return typeof value === "string" && value.trim().length >= minLength;
}

export function isProfileStepComplete(profile: Partial<Profile>) {
  return Boolean(
    hasText(profile.firstName)
      && hasText(profile.dateOfBirth)
      && (profile.gender === "male" || profile.gender === "female")
      && typeof profile.height === "number"
      && hasText(profile.city)
      && hasText(profile.country)
      && hasText(profile.religion)
      && hasText(profile.maritalHistory)
      && typeof profile.hasChildren === "boolean"
      && hasText(profile.wantsChildren)
      && hasText(profile.smoking)
      && hasText(profile.drinking)
      && hasText(profile.aboutMe, 50)
      && hasText(profile.lookingFor, 50)
      && hasText(profile.relocationWillingness)
      && Array.isArray(profile.languages)
      && profile.languages.some((language) => hasText(language.language) && hasText(language.level))
      && Array.isArray(profile.interests)
      && profile.interests.length > 0
  );
}

export async function getOnboardingStep(): Promise<OnboardingStep> {
  let profileResponse;
  try {
    profileResponse = await api.getProfileResponse();
  } catch (error) {
    if (error instanceof ApiError && error.statusCode === 404) {
      return "profile";
    }
    throw error;
  }

  if (!profileResponse.profile.isProfileComplete && !isProfileStepComplete(profileResponse.profile)) {
    return "profile";
  }

  try {
    await api.getPreferences();
  } catch (error) {
    if (error instanceof ApiError && error.statusCode === 404) {
      return "preferences";
    }
    throw error;
  }

  return "complete";
}

export async function resolveAuthenticatedPath() {
  const step = await getOnboardingStep();
  return step === "complete" ? "/app/match" : onboardingPath(step);
}

export function isUnauthorizedApiError(error: unknown): boolean {
  return error instanceof ApiError && error.statusCode === 401;
}

export const ONBOARDING_STATUS_QUERY_KEY = ["onboarding-status"] as const;

export function onboardingStatusQueryOptions({
  authReady,
  isAuthenticated,
}: {
  authReady: boolean;
  isAuthenticated: boolean;
}) {
  return {
    queryKey: ONBOARDING_STATUS_QUERY_KEY,
    queryFn: getOnboardingStep,
    enabled: authReady && isAuthenticated,
    retry: (failureCount: number, error: unknown) => {
      if (isUnauthorizedApiError(error)) return false;
      return failureCount < 2;
    },
    staleTime: 30_000,
  };
}
